"use client"

import { useState, useEffect, useCallback } from "react"
import { CheckCircle2, Circle, CheckSquare, RefreshCw, X, Loader2, Calendar, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { format } from "date-fns"
import { pl } from "date-fns/locale"

interface TaskAssignment {
  id: string
  order: number
  isCompleted?: boolean
  completedAt?: string | null
  completedBy?: { id: string; username: string; avatarUrl: string | null } | null
  task: {
    id: string
    title: string
    description?: string | null
    projectNumber: string | null
    status: string
    client: { id: string; name: string } | null
    clientImageUrl: string | null
  }
}

interface CustomAssignment {
  id: string
  title: string
  order: number
  isRecurring?: boolean
  sourceRecurringId?: string | null
  isCompleted?: boolean
  completedAt?: string | null
  completedBy?: { id: string; username: string; avatarUrl: string | null } | null
}

interface MyAssignmentsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function MyAssignmentsModal({ isOpen, onClose }: MyAssignmentsModalProps) {
  const [loading, setLoading] = useState(true)
  const [completingId, setCompletingId] = useState<string | null>(null)
  const [isCompletingAll, setIsCompletingAll] = useState(false)
  const [taskAssignments, setTaskAssignments] = useState<TaskAssignment[]>([])
  const [customAssignments, setCustomAssignments] = useState<CustomAssignment[]>([])

  const todayStr = new Date().toISOString().slice(0, 10)

  const fetchMyAssignments = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetch(`/api/assignments/my-day?day=${todayStr}`)
      if (res.ok) {
        const data = await res.json()
        setTaskAssignments(data.taskAssignments || [])
        setCustomAssignments(data.customAssignments || [])
      }
    } catch (error) {
      console.error("Error fetching my assignments:", error)
      toast.error("Nie udało się pobrać przydziałów")
    } finally {
      setLoading(false)
    }
  }, [todayStr])

  useEffect(() => {
    if (isOpen) {
      fetchMyAssignments()
    }
  }, [isOpen, fetchMyAssignments])

  const toggleComplete = async (type: "task" | "custom", id: string, currentCompleted: boolean) => {
    try {
      setCompletingId(id)
      const nextCompleted = !currentCompleted
      const res = await fetch("/api/assignments/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, id, isCompleted: nextCompleted })
      })

      if (res.ok) {
        toast.success(nextCompleted ? "Oznaczono jako wykonane!" : "Cofnięto wykonanie")
        await fetchMyAssignments()
      } else {
        toast.error("Błąd aktualizacji przydziału")
      }
    } catch (error) {
      toast.error("Nie udało się połączyć z serwerem")
    } finally {
      setCompletingId(null)
    }
  }

  const handleCompleteAll = async () => {
    try {
      setIsCompletingAll(true)
      const res = await fetch("/api/assignments/complete-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ day: todayStr })
      })

      if (res.ok) {
        toast.success("Oznaczono wszystkie przydziały jako wykonane! 🎉")
        await fetchMyAssignments()
      } else {
        toast.error("Błąd podczas zapisywania")
      }
    } catch (error) {
      toast.error("Błąd sieci")
    } finally {
      setIsCompletingAll(false)
    }
  }

  if (!isOpen) return null

  const allItemsCount = taskAssignments.length + customAssignments.length
  const completedTasksCount = taskAssignments.filter(t => t.isCompleted).length
  const completedCustomsCount = customAssignments.filter(c => c.isCompleted).length
  const totalCompleted = completedTasksCount + completedCustomsCount
  const allDone = allItemsCount > 0 && totalCompleted === allItemsCount

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200 dark:border-zinc-800 flex flex-col max-h-[90vh]">

        {/* Nagłówek modala */}
        <div className="p-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-md rounded-2xl">
                <CheckSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white leading-tight">Moje Przydziały na Dziś</h2>
                <div className="flex items-center gap-2 text-xs text-emerald-100 mt-0.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{format(new Date(), "EEEE, d MMMM yyyy", { locale: pl })}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Statystyki postępu */}
          {allItemsCount > 0 && (
            <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-between">
              <div className="text-xs font-medium text-emerald-100">
                Postęp: <span className="font-bold text-white">{totalCompleted} / {allItemsCount}</span> wykonanych
              </div>
              {totalCompleted < allItemsCount && (
                <button
                  onClick={handleCompleteAll}
                  disabled={isCompletingAll}
                  className="px-3 py-1.5 bg-white text-emerald-800 hover:bg-emerald-50 rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95 flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isCompletingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-emerald-600" />}
                  Zaznacz wszystkie
                </button>
              )}
            </div>
          )}
        </div>

        {/* Lista przydziałów */}
        <div className="p-4 overflow-y-auto space-y-3 flex-1">
          {loading ? (
            <div className="py-12 text-center text-slate-500 dark:text-zinc-400 space-y-2">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-600" />
              <p className="text-sm font-medium">Ładowanie Twoich przydziałów…</p>
            </div>
          ) : allItemsCount === 0 ? (
            <div className="py-12 text-center text-slate-500 dark:text-zinc-400 space-y-2">
              <CheckCircle2 className="w-12 h-12 text-emerald-500/50 mx-auto" />
              <p className="text-base font-semibold text-slate-800 dark:text-zinc-200">Brak przydziałów na dziś</p>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Nie masz jeszcze przypisanych zadań na dzisiejszy dzień.</p>
            </div>
          ) : (
            <>
              {allDone && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-2xl text-center space-y-1">
                  <span className="text-2xl">🎉</span>
                  <h3 className="font-bold text-emerald-900 dark:text-emerald-300">Wszystko wykonane!</h3>
                  <p className="text-xs text-emerald-700 dark:text-emerald-400">Super robota, wszystkie Twoje dzisiejsze przydziały zostały zrealizowane.</p>
                </div>
              )}

              {/* Custom Assignments */}
              {customAssignments.map((c) => {
                const isUpdating = completingId === c.id
                return (
                  <div
                    key={c.id}
                    onClick={() => toggleComplete("custom", c.id, !!c.isCompleted)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer select-none flex items-start gap-3.5 active:scale-[0.99] ${
                      c.isCompleted
                        ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/40"
                        : "bg-slate-50 hover:bg-slate-100 dark:bg-zinc-800/60 dark:hover:bg-zinc-800 border-slate-200 dark:border-zinc-700/60 shadow-sm"
                    }`}
                  >
                    <div className="pt-0.5 shrink-0">
                      {isUpdating ? (
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                      ) : c.isCompleted ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 fill-emerald-100 dark:fill-emerald-950" />
                      ) : (
                        <Circle className="w-6 h-6 text-slate-400 dark:text-zinc-500 hover:text-emerald-600 transition-colors" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-base font-semibold leading-snug break-words ${
                          c.isCompleted
                            ? "line-through text-slate-500 dark:text-zinc-400"
                            : "text-slate-900 dark:text-white"
                        }`}>
                          {c.title}
                        </span>

                        {c.isRecurring && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-100/80 dark:bg-amber-950/50 px-2 py-0.5 rounded-full border border-amber-300 dark:border-amber-800/60">
                            <RefreshCw className="w-3 h-3 animate-spin-slow" />
                            Powtarzalny
                          </span>
                        )}
                      </div>

                      {c.isCompleted && c.completedAt && (
                        <div className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1 font-medium">
                          <span>✓ Wykonano {format(new Date(c.completedAt), "HH:mm")}</span>
                          {c.completedBy && <span>przez {c.completedBy.username}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Task Assignments */}
              {taskAssignments.map((a) => {
                const isUpdating = completingId === a.id
                return (
                  <div
                    key={a.id}
                    onClick={() => toggleComplete("task", a.id, !!a.isCompleted)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer select-none flex items-start gap-3.5 active:scale-[0.99] ${
                      a.isCompleted
                        ? "bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/40"
                        : "bg-slate-50 hover:bg-slate-100 dark:bg-zinc-800/60 dark:hover:bg-zinc-800 border-slate-200 dark:border-zinc-700/60 shadow-sm"
                    }`}
                  >
                    <div className="pt-0.5 shrink-0">
                      {isUpdating ? (
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
                      ) : a.isCompleted ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 fill-emerald-100 dark:fill-emerald-950" />
                      ) : (
                        <Circle className="w-6 h-6 text-slate-400 dark:text-zinc-500 hover:text-emerald-600 transition-colors" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-base font-semibold leading-snug break-words ${
                          a.isCompleted
                            ? "line-through text-slate-500 dark:text-zinc-400"
                            : "text-slate-900 dark:text-white"
                        }`}>
                          {a.task.title}
                        </span>

                        {a.task.projectNumber && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                            #{a.task.projectNumber}
                          </span>
                        )}
                      </div>

                      {a.task.client && (
                        <div className="text-xs text-slate-500 dark:text-zinc-400 mt-1 font-medium">
                          Klient: {a.task.client.name}
                        </div>
                      )}

                      {!!a.task.description && (
                        <p className="text-xs text-slate-600 dark:text-zinc-400 mt-1 line-clamp-2">
                          {a.task.description}
                        </p>
                      )}

                      {a.isCompleted && a.completedAt && (
                        <div className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1 font-medium">
                          <span>✓ Wykonano {format(new Date(a.completedAt), "HH:mm")}</span>
                          {a.completedBy && <span>przez {a.completedBy.username}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>

        {/* Stopka modala */}
        <div className="p-4 bg-slate-50 dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold transition-all"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}
